import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import ConfirmDialog from "components/common/ConfirmDialog";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import Loading from "components/Loading";
import {
  COUNTRIES,
  COUNTRY_UNSET,
  countryLabel,
  normalizeCountryCode,
} from "constants/countries";
import {
  PARTNER_TIERS,
  normalizePartnerTier,
  partnerTierRequiresFoundingPartner,
} from "constants/partnerTiers";
import {
  MANUAL_ADJUSTMENT,
  SETTLEMENT_RAIL_NONE,
  SETTLEMENT_RAIL_OPTIONS,
  isAttributableLedgerOrg,
  settlementRailForCreate,
} from "constants/partnerLedger";
import { useAuth } from "hooks/useAuth";
import { setInstitutionCountry } from "services/institutionCountryService";
import { setInstitutionPartnerTier } from "services/partnerTierService";
import {
  createPartnerLedgerEntry,
  getHttpsCallableErrorMessage,
} from "services/partnerLedgerService";
import { db } from "services/firebase";

const OrganizationsPage = () => {
  const { isGlobalAdmin } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tierDrafts, setTierDrafts] = useState({});
  const [pendingChange, setPendingChange] = useState(null);
  const [tierSaving, setTierSaving] = useState(false);
  const [ledgerSubmitting, setLedgerSubmitting] = useState(false);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      orgName: "",
      adminEmail: "",
      parentOrgId: "",
      llmMonthlyTokenLimit: 500000,
    },
  });

  const {
    control: ledgerControl,
    handleSubmit: handleLedgerSubmit,
    reset: resetLedger,
    watch: watchLedger,
    setValue: setLedgerValue,
  } = useForm({
    defaultValues: {
      foundingPartnerOrgId: "",
      attributedOrgId: "",
      eventType: MANUAL_ADJUSTMENT,
      amount: "",
      currency: "USD",
      description: "",
      settlementRail: SETTLEMENT_RAIL_NONE,
    },
  });

  const selectedFoundingPartnerOrgId = watchLedger("foundingPartnerOrgId");
  const selectedAttributedOrgId = watchLedger("attributedOrgId");

  const loadOrgs = useCallback(async () => {
    setLoadingOrgs(true);
    setErrorMessage("");
    try {
      const snap = await db.collection("organizations").limit(200).get();
      const list = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      list.sort((a, b) =>
        String(a.name || a.id).localeCompare(String(b.name || b.id)),
      );
      setOrgs(list);
      const drafts = {};
      list.forEach((org) => {
        drafts[org.id] = {
          partnerTier: normalizePartnerTier(org.partnerTier),
          foundingPartnerId: org.foundingPartnerId || "",
          countryCode: normalizeCountryCode(org.countryCode) || COUNTRY_UNSET,
        };
      });
      setTierDrafts(drafts);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load organizations.");
    } finally {
      setLoadingOrgs(false);
    }
  }, []);

  useEffect(() => {
    if (!isGlobalAdmin) return;
    loadOrgs();
  }, [isGlobalAdmin, loadOrgs]);

  const foundingOptions = useMemo(
    () =>
      orgs.filter(
        (org) => normalizePartnerTier(org.partnerTier) === "founding",
      ),
    [orgs],
  );

  const orgNameById = useMemo(() => {
    const map = {};
    orgs.forEach((org) => {
      map[org.id] = org.name || org.id;
    });
    return map;
  }, [orgs]);

  const attributedOrgOptions = useMemo(() => {
    if (!selectedFoundingPartnerOrgId) return [];
    return orgs.filter((org) =>
      isAttributableLedgerOrg({
        orgId: org.id,
        partnerTier: org.partnerTier,
        foundingPartnerId: org.foundingPartnerId,
        foundingPartnerOrgId: selectedFoundingPartnerOrgId,
      }),
    );
  }, [orgs, selectedFoundingPartnerOrgId]);

  useEffect(() => {
    if (
      selectedAttributedOrgId &&
      attributedOrgOptions.some((org) => org.id === selectedAttributedOrgId)
    ) {
      return;
    }
    setLedgerValue("attributedOrgId", attributedOrgOptions[0]?.id || "");
  }, [attributedOrgOptions, selectedAttributedOrgId, setLedgerValue]);

  if (!isGlobalAdmin) {
    return <Alert severity="error">Global admin access required.</Alert>;
  }

  const onLedgerCreate = async (values) => {
    setLedgerSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const amount = Number(values.amount);
      if (!Number.isInteger(amount) || amount === 0) {
        setErrorMessage(
          "Amount must be a non-zero integer in minor units (e.g. cents).",
        );
        return;
      }
      const result = await createPartnerLedgerEntry({
        foundingPartnerOrgId: values.foundingPartnerOrgId,
        attributedOrgId: values.attributedOrgId,
        eventType: MANUAL_ADJUSTMENT,
        amount,
        currency: String(values.currency || "").trim().toUpperCase(),
        description: String(values.description || "").trim(),
        settlementRail: settlementRailForCreate(values.settlementRail),
      });
      setSuccessMessage(
        `Authoritative ledger entry recorded (${result.entryId}). This is an accounting record only — no money was moved.`,
      );
      resetLedger({
        foundingPartnerOrgId: values.foundingPartnerOrgId,
        attributedOrgId: values.attributedOrgId,
        eventType: MANUAL_ADJUSTMENT,
        amount: "",
        currency: values.currency || "USD",
        description: "",
        settlementRail: SETTLEMENT_RAIL_NONE,
      });
    } catch (err) {
      setErrorMessage(
        getHttpsCallableErrorMessage(err, "Could not record ledger entry."),
      );
    } finally {
      setLedgerSubmitting(false);
    }
  };

  const onProvision = async (values) => {
    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const callable = firebase
        .app()
        .functions("us-central1")
        .httpsCallable("provisionOrganization");
      const result = await callable({
        orgName: values.orgName.trim(),
        adminEmail: values.adminEmail.trim(),
        parentOrgId: values.parentOrgId.trim() || null,
        llmMonthlyTokenLimit: Number(values.llmMonthlyTokenLimit) || 500000,
      });
      setSuccessMessage(
        `Organization provisioned: ${result.data.organizationId}`,
      );
      reset();
      await loadOrgs();
    } catch (err) {
      setErrorMessage(err.message || "Provisioning failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const updateDraft = (orgId, patch) => {
    setTierDrafts((prev) => {
      const current = prev[orgId] || {
        partnerTier: "none",
        foundingPartnerId: "",
        countryCode: COUNTRY_UNSET,
      };
      const next = { ...current, ...patch };
      if (!partnerTierRequiresFoundingPartner(next.partnerTier)) {
        next.foundingPartnerId = "";
      }
      return { ...prev, [orgId]: next };
    });
  };

  const requestTierSave = (org) => {
    const draft = tierDrafts[org.id];
    if (!draft) return;
    const currentTier = normalizePartnerTier(org.partnerTier);
    const currentFounding = org.foundingPartnerId || "";
    const currentCountry = normalizeCountryCode(org.countryCode);
    const nextCountry = normalizeCountryCode(draft.countryCode);
    const tierChanged =
      draft.partnerTier !== currentTier ||
      (draft.foundingPartnerId || "") !== currentFounding;
    const countryChanged = nextCountry !== currentCountry;
    if (!tierChanged && !countryChanged) {
      setErrorMessage("No changes to save for this organization.");
      return;
    }
    if (
      tierChanged &&
      partnerTierRequiresFoundingPartner(draft.partnerTier) &&
      !draft.foundingPartnerId
    ) {
      setErrorMessage(
        "Select a Fusion Founder before saving a Fusion or Fusion Affiliate tier.",
      );
      return;
    }
    setErrorMessage("");
    setPendingChange({
      organizationId: org.id,
      name: org.name || org.id,
      partnerTier: draft.partnerTier,
      foundingPartnerId: partnerTierRequiresFoundingPartner(draft.partnerTier)
        ? draft.foundingPartnerId
        : null,
      previousPartnerTier: currentTier,
      previousFoundingPartnerId: currentFounding || null,
      countryCode: nextCountry,
      previousCountryCode: currentCountry,
      tierChanged,
      countryChanged,
    });
  };

  const confirmTierSave = async () => {
    if (!pendingChange) return;
    setTierSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      if (pendingChange.tierChanged) {
        await setInstitutionPartnerTier({
          organizationId: pendingChange.organizationId,
          partnerTier: pendingChange.partnerTier,
          foundingPartnerId: pendingChange.foundingPartnerId,
        });
      }
      if (pendingChange.countryChanged) {
        await setInstitutionCountry({
          orgId: pendingChange.organizationId,
          countryCode: pendingChange.countryCode,
        });
      }
      const parts = [];
      if (pendingChange.tierChanged) {
        parts.push(`partner tier to ${pendingChange.partnerTier}`);
      }
      if (pendingChange.countryChanged) {
        parts.push(`country to ${countryLabel(pendingChange.countryCode)}`);
      }
      setSuccessMessage(
        `Updated ${pendingChange.name}: ${parts.join(" and ")}.`,
      );
      setPendingChange(null);
      await loadOrgs();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to update organization.");
    } finally {
      setTierSaving(false);
    }
  };

  if (loadingOrgs && !orgs.length) {
    return <Loading page />;
  }

  return (
    <Box>
      <DashboardPageHeader
        title="Organizations"
        subtitle="Provision client orgs, assign GNT partner tiers, and set institution country (platform admin only)."
      />
      {successMessage && (
        <Alert
          severity="success"
          onClose={() => setSuccessMessage("")}
          sx={{ mb: 2 }}
        >
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage("")}
          sx={{ mb: 2 }}
        >
          {errorMessage}
        </Alert>
      )}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Provision organization
        </Typography>
        <Stack
          component="form"
          spacing={2}
          onSubmit={handleSubmit(onProvision)}
          sx={{ maxWidth: 480 }}
        >
          <Controller
            name="orgName"
            control={control}
            rules={{ required: "Organization name is required." }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Organization name"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="adminEmail"
            control={control}
            rules={{ required: "Admin email is required." }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Org admin email"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="parentOrgId"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Parent org ID (optional)" />
            )}
          />
          <Controller
            name="llmMonthlyTokenLimit"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Monthly LLM token limit"
                type="number"
              />
            )}
          />
          <LoadingButton type="submit" variant="contained" loading={submitting}>
            Provision
          </LoadingButton>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Organizations & partner tiers
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Partner tier is a formal business relationship. Country is ISO 3166-1
          alpha-2 institution metadata. Changes are audited. These fields do
          not yet affect billing, ads, ClinicalHUD2, or prompt governance.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Partner tier</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Fusion Founder</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgs.map((org) => {
                const draft = tierDrafts[org.id] || {
                  partnerTier: "none",
                  foundingPartnerId: "",
                  countryCode: COUNTRY_UNSET,
                };
                const needsFounding = partnerTierRequiresFoundingPartner(
                  draft.partnerTier,
                );
                return (
                  <TableRow key={org.id}>
                    <TableCell>{org.name || "—"}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {org.id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id={`tier-${org.id}`}>Tier</InputLabel>
                        <Select
                          labelId={`tier-${org.id}`}
                          label="Tier"
                          value={draft.partnerTier}
                          onChange={(e) =>
                            updateDraft(org.id, {
                              partnerTier: e.target.value,
                            })
                          }
                        >
                          {PARTNER_TIERS.map((tier) => (
                            <MenuItem key={tier.value} value={tier.value}>
                              {tier.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id={`country-${org.id}`}>Country</InputLabel>
                        <Select
                          labelId={`country-${org.id}`}
                          label="Country"
                          value={draft.countryCode || COUNTRY_UNSET}
                          onChange={(e) =>
                            updateDraft(org.id, {
                              countryCode: e.target.value,
                            })
                          }
                        >
                          <MenuItem value={COUNTRY_UNSET}>
                            <em>Not set</em>
                          </MenuItem>
                          {COUNTRIES.map((country) => (
                            <MenuItem key={country.value} value={country.value}>
                              {country.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <FormControl fullWidth size="small" disabled={!needsFounding}>
                        <InputLabel id={`fp-${org.id}`}>
                          Fusion Founder
                        </InputLabel>
                        <Select
                          labelId={`fp-${org.id}`}
                          label="Fusion Founder"
                          value={needsFounding ? draft.foundingPartnerId : ""}
                          onChange={(e) =>
                            updateDraft(org.id, {
                              foundingPartnerId: e.target.value,
                            })
                          }
                        >
                          <MenuItem value="">
                            <em>Select…</em>
                          </MenuItem>
                          {foundingOptions
                            .filter((fp) => fp.id !== org.id)
                            .map((fp) => (
                              <MenuItem key={fp.id} value={fp.id}>
                                {fp.name || fp.id}
                              </MenuItem>
                            ))}
                          {needsFounding &&
                            draft.foundingPartnerId &&
                            !foundingOptions.some(
                              (fp) => fp.id === draft.foundingPartnerId,
                            ) && (
                              <MenuItem value={draft.foundingPartnerId}>
                                {orgNameById[draft.foundingPartnerId] ||
                                  draft.foundingPartnerId}{" "}
                                (not currently founding)
                              </MenuItem>
                            )}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="right">
                      <LoadingButton
                        size="small"
                        variant="contained"
                        onClick={() => requestTierSave(org)}
                        disabled={tierSaving}
                      >
                        Save
                      </LoadingButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!orgs.length && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">
                      No organizations found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Authoritative partner-ledger entry
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Platform-admin only. Creates an immutable entry flagged as
          authoritative (source: admin) for a Fusion Founder and one of its
          linked orgs. This records accounting only — it does not move money, and
          settlement is not configured yet.
        </Typography>
        <Stack
          component="form"
          spacing={2}
          onSubmit={handleLedgerSubmit(onLedgerCreate)}
          sx={{ maxWidth: 520 }}
        >
          <Controller
            name="foundingPartnerOrgId"
            control={ledgerControl}
            rules={{ required: "Fusion Founder is required." }}
            render={({ field, fieldState }) => (
              <FormControl fullWidth error={Boolean(fieldState.error)}>
                <InputLabel id="admin-ledger-founding">
                  Fusion Founder
                </InputLabel>
                <Select
                  {...field}
                  labelId="admin-ledger-founding"
                  label="Fusion Founder"
                >
                  {foundingOptions.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name || org.id}
                    </MenuItem>
                  ))}
                </Select>
                {fieldState.error ? (
                  <Typography variant="caption" color="error">
                    {fieldState.error.message}
                  </Typography>
                ) : null}
              </FormControl>
            )}
          />
          <Controller
            name="attributedOrgId"
            control={ledgerControl}
            rules={{ required: "Attributed organization is required." }}
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                error={Boolean(fieldState.error)}
                disabled={!selectedFoundingPartnerOrgId}
              >
                <InputLabel id="admin-ledger-attributed">
                  Attributed organization
                </InputLabel>
                <Select
                  {...field}
                  labelId="admin-ledger-attributed"
                  label="Attributed organization"
                >
                  {attributedOrgOptions.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name || org.id} (
                      {normalizePartnerTier(org.partnerTier)})
                    </MenuItem>
                  ))}
                </Select>
                {fieldState.error ? (
                  <Typography variant="caption" color="error">
                    {fieldState.error.message}
                  </Typography>
                ) : null}
              </FormControl>
            )}
          />
          <Controller
            name="eventType"
            control={ledgerControl}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="admin-ledger-event-type">Event type</InputLabel>
                <Select
                  {...field}
                  labelId="admin-ledger-event-type"
                  label="Event type"
                >
                  <MenuItem value={MANUAL_ADJUSTMENT}>
                    Manual adjustment
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          />
          <Controller
            name="amount"
            control={ledgerControl}
            rules={{ required: "Amount is required." }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Amount (minor units)"
                type="number"
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ||
                  "Non-zero integer in minor units (e.g. cents)."
                }
              />
            )}
          />
          <Controller
            name="currency"
            control={ledgerControl}
            rules={{
              required: "Currency is required.",
              pattern: {
                value: /^[A-Za-z]{3}$/,
                message: "Use a 3-letter ISO 4217 code.",
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Currency"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="description"
            control={ledgerControl}
            rules={{ required: "Description is required." }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Description"
                multiline
                minRows={2}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="settlementRail"
            control={ledgerControl}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="admin-ledger-settlement-rail">
                  Settlement rail
                </InputLabel>
                <Select
                  {...field}
                  labelId="admin-ledger-settlement-rail"
                  label="Settlement rail"
                >
                  {SETTLEMENT_RAIL_OPTIONS.map((option) => (
                    <MenuItem key={option.value || "none"} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          <LoadingButton
            type="submit"
            variant="contained"
            loading={ledgerSubmitting}
            disabled={!foundingOptions.length}
          >
            Record authoritative entry
          </LoadingButton>
        </Stack>
      </Paper>

      <ConfirmDialog
        open={Boolean(pendingChange)}
        onClose={() => {
          if (!tierSaving) setPendingChange(null);
        }}
        title={
          pendingChange?.tierChanged && pendingChange?.countryChanged
            ? "Confirm organization updates"
            : pendingChange?.countryChanged
              ? "Confirm country change"
              : "Confirm partner tier change"
        }
        message={
          pendingChange
            ? [
                pendingChange.tierChanged
                  ? `Change ${pendingChange.name} from ${pendingChange.previousPartnerTier} to ${pendingChange.partnerTier}${
                      pendingChange.foundingPartnerId
                        ? ` (Fusion Founder: ${
                            orgNameById[pendingChange.foundingPartnerId] ||
                            pendingChange.foundingPartnerId
                          })`
                        : ""
                    }. This is a formal business relationship and will be audited.`
                  : "",
                pendingChange.countryChanged
                  ? `Set country for ${pendingChange.name} from ${countryLabel(
                      pendingChange.previousCountryCode,
                    )} to ${countryLabel(pendingChange.countryCode)}. This is institution metadata and will be audited.`
                  : "",
              ]
                .filter(Boolean)
                .join(" ")
            : ""
        }
        confirmLabel={
          pendingChange?.tierChanged && pendingChange?.countryChanged
            ? "Save changes"
            : pendingChange?.countryChanged
              ? "Save country"
              : "Save partner tier"
        }
        onConfirm={confirmTierSave}
        loading={tierSaving}
      />
    </Box>
  );
};

export default OrganizationsPage;
